<script lang="ts">
  import { onMount } from 'svelte';
  import { createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { boardApi } from '$lib/api/board';
  import { getErrorMessage } from '$lib/api/client';
  import type { BoardCard, BoardUpdateStatus } from '$lib/types';
  import { locale, t } from '$lib/i18n';
  import { boardDate, boardRelativeDate } from '$lib/utils/board';
  import { boardUpdateAuthorLabel } from '$lib/utils/systemLabels';
  import Modal from '$lib/components/ui/Modal.svelte';
  import BoardDueBadge from '$lib/components/ui/BoardDueBadge.svelte';
  import BoardSeenBadge from '$lib/components/ui/BoardSeenBadge.svelte';
  import { session } from '$lib/state/session.svelte';
  import { boardViewIsAssignee } from '$lib/utils/boardSeen';
  import BoardDateInput from '$lib/components/ui/BoardDateInput.svelte';
  import { canReturnBoardHelper, saveBoardProgress, selectsRequester, unresolvedBoardHelpers } from '$lib/utils/boardHandoff';

  let { card, team = false, initialStatus = 'on_track', initialText = '', onClose, onSaved }: { card: BoardCard; team?: boolean; initialStatus?: BoardUpdateStatus; initialText?: string; onClose: () => void; onSaved: () => void } = $props();
  const client = useQueryClient();
  const updates = createQuery(() => ({ queryKey: ['board-updates', team, card.id], queryFn: () => boardApi.updates(card.id, team) }));
  // Each opening is keyed by its card/action in the parent.
  // svelte-ignore state_referenced_locally
  let text = $state(initialText);
  // svelte-ignore state_referenced_locally
  let status = $state<BoardUpdateStatus>(initialStatus);
  const statuses: BoardUpdateStatus[] = ['on_track', 'waiting', 'blocked', 'done'];
  let waitingOn = $state('');
  let nextFollowup = $state('');
  let saving = $state(false);
  let error = $state('');
  let textarea: HTMLTextAreaElement;
  let now = $state(Date.now());
  let blockMode = $state<'helpers' | 'external'>('helpers');
  let externalName = $state('');
  type HelperDraft = { query: string; user_id: number | null; name: string; title: string; matches: { id: number; name: string }[]; version: number };
  const newHelper = (): HelperDraft => ({ query: '', user_id: null, name: '', title: '', matches: [], version: 0 });
  let helpers = $state<HelperDraft[]>([newHelper()]);
  const parentSelected = $derived(blockMode === 'helpers' && selectsRequester(card, helpers));
  const blockValid = $derived(blockMode === 'external' ? Boolean(externalName.trim()) : !parentSelected && helpers.length > 0 && helpers.every((helper) => helper.user_id && helper.title.trim()));
  const unresolved = $derived(unresolvedBoardHelpers(card));
  let returned = $state(false);
  async function lookupHelper(helper: HelperDraft) {
    helper.user_id = null; helper.name = ''; helper.matches = [];
    const version = ++helper.version;
    if (!helper.query.trim()) return;
    try {
      const matches = await boardApi.mentions(helper.query.trim());
      if (version === helper.version) helper.matches = matches;
    } catch (err) { if (version === helper.version) error = getErrorMessage(err); }
  }
  async function saved() {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['board'] }),
      client.invalidateQueries({ queryKey: ['board-team'] }),
      client.invalidateQueries({ queryKey: ['board-team-activity'] }),
      client.invalidateQueries({ queryKey: ['board-updates'] })
    ]);
    onSaved();
  }
  async function returnToRequester() {
    if (saving || returned || !canReturnBoardHelper(card) || !text.trim()) return;
    if (!confirm(t('board.return.confirm', { owner: card.blocking!.user_name, title: card.blocking!.title }))) return;
    saving = true; error = '';
    try {
      card = await boardApi.returnToRequester(card.id, text.trim(), team);
      returned = true;
      await saved();
      text = ''; waitingOn = ''; nextFollowup = '';
      onClose();
    } catch (err) { error = getErrorMessage(err); }
    finally { saving = false; }
  }
  onMount(() => {
    if (boardViewIsAssignee(card, session.me?.id)) void boardApi.recordView(card.id, 'card_open').catch(() => {});
    textarea?.focus();
    const timer = setInterval(() => { now = Date.now(); }, 60_000);
    return () => clearInterval(timer);
  });

  async function save() {
    if (!text.trim() || saving || returned || (status === 'blocked' && !blockValid)) return;
    saving = true;
    error = '';
    try {
      const update = { text: text.trim(), waiting_on: waitingOn.trim(), next_followup: nextFollowup || null };
      const result = await saveBoardProgress(boardApi, card.id, { ...update, status }, team, {
        helpers: blockMode === 'helpers' ? helpers.map((helper) => ({ user_id: helper.user_id!, title: helper.title.trim() })) : [],
        ...(blockMode === 'external' ? { external: { name: externalName.trim() } } : {})
      });
      if (status === 'blocked') card = result as BoardCard;
      await saved();
      text = ''; waitingOn = ''; nextFollowup = '';
      textarea?.focus();
    } catch (err) { error = getErrorMessage(err); }
    finally { saving = false; }
  }
</script>

<Modal title={t('board.updates.title')} {onClose} closable={!saving}>
  <h3 class="break-words font-semibold text-navy">{card.title}</h3>
  <BoardDueBadge {card} />
  <BoardSeenBadge {card} />
  {#if card.blocking}
    <section class="mt-3 rounded-lg border border-toyama-teal p-3 space-y-2" aria-label={t('board.return.context')}>
      <p class="text-sm font-semibold text-navy">{t('board.return.helping', { owner: card.blocking.user_name, title: card.blocking.title })}</p>
      {#if card.col !== 'done' && !returned}
        <p class="text-sm text-dark-gray">{t('board.return.guidance')}</p>
        <button type="button" class="btn-secondary min-h-[44px]" disabled={saving || !text.trim() || !canReturnBoardHelper(card)} onclick={returnToRequester}>{t('board.return.action')}</button>
        <p class="text-xs text-dark-gray">{t('board.return.messageRequired')}</p>
      {/if}
    </section>
  {/if}
  {#if unresolved.length || card.blocked_external_name}
    <section class="mt-3 rounded-lg border border-red-300 bg-red-50 p-3" aria-label={t('board.return.blockers')}>
      <p class="text-sm font-semibold text-red-700">{t('board.return.resolveFirst')}</p>
      <ul class="mt-2 text-sm text-navy">{#each unresolved as helper}<li>{helper.user_name}: {helper.title} — {t(`board.column.${helper.col}`)}</li>{/each}</ul>
      {#if card.blocked_external_name}<p class="text-sm">{t('board.blockedExternal', { name: card.blocked_external_name })}</p>{/if}
    </section>
  {/if}
  <form id="board-update-form" class="mt-4 space-y-3" onsubmit={(e) => { e.preventDefault(); save(); }}>
    <div class="flex flex-wrap gap-2" role="group" aria-label={t('board.updates.status')}>
      {#each statuses as option}<button type="button" class="btn-secondary min-h-[44px] {status === option ? '!border-toyama-teal !text-navy' : ''}" aria-pressed={status === option} onclick={() => status = option}>{t(`board.status.${option}`)}</button>{/each}
    </div>
    <label class="label">{t('board.updates.prompt')}<textarea bind:this={textarea} class="input mt-1 min-h-[100px]" rows="3" bind:value={text} required maxlength="1000"></textarea></label>
    {#if status === 'blocked'}
      <div class="space-y-3 rounded-lg border border-toyama-cloudy-gray p-3">
        <div class="flex flex-wrap gap-2" role="group" aria-label={t('board.block.modeLabel')}>
          <button type="button" class="btn-secondary min-h-[44px]" aria-pressed={blockMode === 'helpers'} onclick={() => blockMode = 'helpers'}>{t('board.block.modeHelpers')}</button>
          <button type="button" class="btn-secondary min-h-[44px]" aria-pressed={blockMode === 'external'} onclick={() => blockMode = 'external'}>{t('board.block.modeExternal')}</button>
        </div>
        {#if blockMode === 'external'}
          <label class="label">{t('board.block.externalName')}<input class="input mt-1 min-h-[44px]" bind:value={externalName} maxlength="80" /></label>
          <p class="text-xs text-dark-gray">{t('board.block.externalGuidance')}</p>
        {:else}
          <p class="text-xs text-dark-gray">{t('board.block.firstNameGuidance')}</p>
          {#each helpers as helper, i (helper)}
            <fieldset class="rounded-lg border border-toyama-cloudy-gray p-3">
              <legend>{t('board.block.helperNumber', { n: i + 1 })}</legend>
              <label class="label">{t('board.block.helperFirstName')}<input class="input mt-1 min-h-[44px]" autocomplete="off" bind:value={helper.query} oninput={() => lookupHelper(helper)} /></label>
              {#if helper.name}<p class="mt-2 text-sm">{t('board.block.selectedHelper', { name: helper.name })}</p>{/if}
              <div class="mt-2 flex flex-wrap gap-2" aria-label={t('board.block.matches')}>{#each helper.matches as person (person.id)}<button type="button" class="btn-secondary min-h-[44px]" onclick={() => { helper.version++; helper.user_id = person.id; helper.name = person.name; helper.query = person.name; helper.matches = []; }}>{person.name}</button>{/each}</div>
              <label class="label mt-3">{t('board.block.helperTask')}<input class="input mt-1 min-h-[44px]" bind:value={helper.title} maxlength="200" /></label>
              {#if helpers.length > 1}<button type="button" class="btn-secondary mt-2 min-h-[44px]" onclick={() => helpers = helpers.filter((item) => item !== helper)}>{t('board.block.removeHelper')}</button>{/if}
            </fieldset>
          {/each}
          <button type="button" class="btn-secondary min-h-[44px]" onclick={() => helpers = [...helpers, newHelper()]}>{t('board.block.addHelper')}</button>
          {#if parentSelected}<p class="text-sm font-semibold text-red-700" role="alert">{t('board.return.parentSelected')}</p>{/if}
        {/if}
      </div>
    {/if}
    <label class="label">{t('board.updates.waitingOn')}<input class="input mt-1 min-h-[44px]" bind:value={waitingOn} maxlength="80" /></label>
    <div><BoardDateInput label={t('board.updates.nextFollowup')} bind:value={nextFollowup} /></div>
    {#if error}<p class="tym-error-state" role="alert">{error}</p>{/if}
  </form>
  <h3 class="mb-3 mt-6 font-semibold text-navy">{t('board.updates.timeline')}</h3>
  {#if updates.isPending}<p class="text-sm text-dark-gray">{t('common.loading')}</p>
  {:else if updates.isError}<div class="tym-error-state" role="alert">{getErrorMessage(updates.error)}<button type="button" class="btn-secondary mt-2" onclick={() => updates.refetch()}>{t('common.retry')}</button></div>
  {:else}
    <ol class="space-y-3">
      {#each updates.data ?? [] as update (update.id)}
        <li class="rounded-lg border border-toyama-cloudy-gray p-3 text-sm">
          {#if update.status}<p class="mb-1 text-xs font-semibold text-toyama-teal">{t(`board.status.${update.status}`)}</p>{/if}
          <p class="whitespace-pre-wrap break-words text-navy">{update.text}</p>
          {#if update.waiting_on}<p class="mt-2 break-words text-dark-gray">{t('board.updates.waitingOn')}: {update.waiting_on}</p>{/if}
          {#if update.next_followup}<p class="mt-1 text-dark-gray">{t('board.updates.nextFollowup')}: {boardDate(update.next_followup)}</p>{/if}
          <p class="mt-2 text-xs text-dark-gray">{boardUpdateAuthorLabel(update)} · <time datetime={update.created_at} title={new Date(update.created_at).toLocaleString(locale.current, { timeZone: 'Asia/Kolkata', timeZoneName: 'short' })}>{boardRelativeDate(update.created_at, locale.current, now)}</time></p>
        </li>
      {:else}<li class="text-sm text-dark-gray">{t('board.updates.empty')}</li>{/each}
    </ol>
  {/if}
  {#snippet footer()}
    <button type="submit" form="board-update-form" class="btn-primary min-h-[44px] w-full" disabled={saving || returned || !text.trim() || (status === 'blocked' && !blockValid)}>{saving ? t('common.saving') : t('board.updates.add')}</button>
  {/snippet}
</Modal>
